import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Sprite } from '@agissc/ktag-map';
import LayerVisibility from '../VisiblitySettings';
import useAppState from '../../../AppStore';
import { MapTypeCategory } from '../api/Contracts';
import { useTracking } from '../../../utils/tracking/TrackingContext';
import { useHasAttributeSearch } from './attributeSearch/useHasAttributeearch';

interface DragHandleProps {
  listeners: Record<string, unknown>;
  attributes: Record<string, unknown>;
}

interface SelectedTopicLayerProps {
  id: string;
  title: string;
  onRemove: () => void;
  isVisible: boolean;
  onToggleVisibility: () => void;
  opacity: number;
  onOpacityChanged: (opacity: number) => void;
  source: string;
  isPublic?: boolean;
  hasError?: boolean;
  dragHandleProps?: DragHandleProps;
}

const SelectedTopicLayer = ({
  id,
  title,
  onRemove,
  isVisible,
  onToggleVisibility,
  opacity,
  onOpacityChanged,
  source,
  isPublic = true,
  hasError = false,
  dragHandleProps,
}: SelectedTopicLayerProps) => {
  const { t } = useTranslation('agisviewer');
  const setActiveView = useAppState((state) => state.setActiveView);
  const setCurrentTopicLayerId = useAppState((state) => state.setCurrentTopicLayerId);
  const currentAttributeSearchTopicId = useAppState(
    (state) => state.currentAttributeSearchTopicId,
  );
  const setCurrentAttributeSearchTopicId = useAppState(
    (state) => state.setCurrentAttributeSearchTopicId,
  );
  const { Tracking } = useTracking();
  const { hasAttributeSearch } = useHasAttributeSearch(id);

  const handleSettingOnClick = () => {
    Tracking.Topics.openTopicSettings(id);
    setCurrentTopicLayerId(id);
    setActiveView('mapContent_tool_topic_settings');
  };

  const onAttributeSearchClick = () => {
    Tracking.Topics.openAttributeSearch(id);
    setCurrentTopicLayerId(id);
    setCurrentAttributeSearchTopicId(id);
    setActiveView('mapContent_tool_topic_attribute_search');
  };

  return (
    <>
      <div className="topic-layer__item_title_wrapper">
        <div>
          <div className="topic-layer__item-title-container">
            <span
              className="topic-layer__drag-handle"
              {...dragHandleProps?.listeners}
              {...dragHandleProps?.attributes}
            >
              <Sprite
                id="dots-six-vertical"
                additionalClassNames="topic-layer__frag-icon"
              />
            </span>
            <b>{title}</b>
            {hasError && (
              <div
                title={t('notification_topic_loading_error', { topicLayerName: title })}
              >
                <Sprite additionalClassNames="topic-layer__error-icon" id="attention" />
              </div>
            )}
          </div>

          <div className="topic-layer__item-map-type">
            <span>{t(MapTypeCategory.get(source) ?? '')}</span>
            <div title={t('restricted_layer')}>
              {!isPublic && (
                <Sprite id="lock-closed" additionalClassNames="result-item_type-icon" />
              )}
            </div>
          </div>
        </div>
        {hasAttributeSearch && (
          <IconButton
            disabled={!isVisible}
            onClick={onAttributeSearchClick}
            icon="loupe"
            title={
              isVisible
                ? t('topic_attribute_search')
                : t('topic_attribute_search_disabled')
            }
            additionalClassNames={`topic-layer__item-button${currentAttributeSearchTopicId === id ? ' topic-layer__activeAttributeSearch' : ''}`}
          />
        )}
      </div>

      <IconButton
        onClick={onRemove}
        icon="wastebin"
        title={t('remove_topic_layer')}
        additionalClassNames="topic-layer__item-button"
      />
      <IconButton
        onClick={handleSettingOnClick}
        icon="gear"
        title={t('topic_settings')}
        additionalClassNames="topic-layer__item-button"
      />
      <LayerVisibility
        additionalClassNames="topic-layer__visibility"
        isVisible={isVisible}
        onToggleVisibility={onToggleVisibility}
        onOpacityChanged={onOpacityChanged}
        opacity={opacity}
      />
    </>
  );
};

export default SelectedTopicLayer;
